import { useState, useMemo, useCallback } from 'react';
import { Building2, ClipboardList, DollarSign, Package, TrendingUp, Truck, Wrench } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '@/hooks/useAuth';
import { useFinance } from '@/hooks/useFinance';
import { api } from '@/lib/api';
import { EmptyState, ErrorState, KPICard } from '@mustauto/ui/web';
import { RevenueDonut } from '@/components/RevenueDonut';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { ExportDropdown } from '@/components/ui/export-dropdown';
import { useToast } from '@mustauto/ui/web';
import { SkeletonKPIGrid, SkeletonTable } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@mustauto/ui/web';
import { STATUS_MAP } from '@/lib/status';
import { formatReais } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, Receipt } from 'lucide-react';
import { PayablesSection } from './finance/PayablesSection';
import { InsurerReceivablesSection } from './finance/InsurerReceivablesSection';
import { formatApiError, formatDayMonthBR } from '@mustauto/utils';

type PeriodPreset = 'week' | 'month' | 'quarter' | 'custom';

// ── Contas a Receber (spec 7.2) ──────────────────────────────

interface ReceivableItem {
  id: string;
  number: number;
  customerName: string | null;
  customerPhone: string | null;
  vehicleName: string;
  plate: string;
  /** O que FALTA receber: já sem o desconto e sem as parcelas recebidas. */
  total: number;
  grossTotal: number;
  discount: number;
  paid: number;
  waitingSince: string;
}

function daysWaiting(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** wa.me exige só dígitos com DDI; telefones BR salvos sem +55 ganham o prefixo. */
function waMeUrl(phone: string, message: string): string {
  let digits = phone.replace(/\D/g, '');
  if (!digits.startsWith('55')) digits = `55${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function defaultChargeMessage(item: ReceivableItem, workshopName: string): string {
  const firstName = item.customerName?.split(' ')[0] ?? '';
  // `item.total` é o SALDO. Com parcela já recebida a frase muda: cobrar "no
  // valor de X" de quem pagou metade faz a oficina parecer desorganizada com
  // o próprio caixa.
  const valor =
    item.paid > 0
      ? `restando ${formatReais(item.total)} de um total de ${formatReais(item.grossTotal - item.discount)}`
      : `no valor de ${formatReais(item.total)}`;
  return (
    `Olá${firstName ? ` ${firstName}` : ''}! Aqui é da ${workshopName}. ` +
    `O serviço do seu ${item.vehicleName} (OS #${item.number}) está concluído e aguarda pagamento ` +
    `${valor}. Qualquer dúvida estamos à disposição!`
  );
}

function ReceivablesSection() {
  const { workshops } = useAuth();
  const workshopName = workshops.find((w) => w.isCurrent)?.name ?? 'oficina';
  const [charging, setCharging] = useState<ReceivableItem | null>(null);
  const [message, setMessage] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery<{ total: number; items: ReceivableItem[] }>({
    queryKey: ['finance-receivables'],
    queryFn: async () => (await api.get('/workshops/my/finance/receivables')).data,
  });

  function openCharge(item: ReceivableItem) {
    setCharging(item);
    setMessage(defaultChargeMessage(item, workshopName));
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold text-foreground">
          Contas a Receber
        </CardTitle>
        {data && data.items.length > 0 && (
          <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            {formatReais(data.total)} em aberto
          </span>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonTable rows={3} />
        ) : isError && !data ? (
          /* Com `data` em cache o cabeçalho acima já mostra o total em aberto;
             trocar a lista por um erro deixaria os dois discordando na mesma
             tela. O erro entra só quando não há nada a mostrar. */
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            icon={<Receipt size={28} />}
            title="Nenhuma conta a receber"
            description="Quando uma OS entrar em Aguardando Pagamento, ela aparece aqui com a ação de cobrança."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>OS</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead className="text-right">Em aberto</TableHead>
                <TableHead className="text-right">Aguardando</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item) => {
                const days = daysWaiting(item.waitingSince);
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.customerName ?? '—'}</TableCell>
                    <TableCell>#{item.number}</TableCell>
                    <TableCell>{item.vehicleName} · {item.plate}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatReais(item.total)}
                      {/* A OS paga pela metade mostra um valor menor que o do
                          orçamento; sem esta linha nada explica a diferença. */}
                      {item.paid > 0 && (
                        <div className="text-xs font-normal text-muted-foreground">
                          pago {formatReais(item.paid)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={`text-right text-sm ${days > 7 ? 'font-semibold text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                      {days === 0 ? 'hoje' : `${days} dia${days > 1 ? 's' : ''}`}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => openCharge(item)}
                        disabled={!item.customerPhone}
                        title={item.customerPhone ? undefined : 'Cliente sem telefone cadastrado'}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <MessageCircle size={14} />
                        Cobrar
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Modal Cobrar: mensagem pré-formatada EDITÁVEL → abre WhatsApp (spec 7.2) */}
      {charging && charging.customerPhone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
            <h3 className="mb-1 text-base font-semibold text-foreground">
              Cobrar {charging.customerName ?? 'cliente'} — OS #{charging.number}
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Revise a mensagem antes de abrir o WhatsApp.
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="mb-4 w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground outline-none focus:border-primary"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setCharging(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <a
                href={waMeUrl(charging.customerPhone, message)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setCharging(null)}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                <MessageCircle size={16} />
                Abrir WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function getRange(preset: PeriodPreset): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);

  if (preset === 'week') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { from: d.toISOString().slice(0, 10), to };
  }
  if (preset === 'quarter') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 3);
    return { from: d.toISOString().slice(0, 10), to };
  }
  // month (default)
  const d = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: d.toISOString().slice(0, 10), to };
}

export function FinancePage() {
  const { user } = useAuth();
  const [preset, setPreset] = useState<PeriodPreset>('month');
  const [exportLoading, setExportLoading] = useState(false);
  const { toast } = useToast();

  const range = useMemo(() => getRange(preset), [preset]);
  const { data, isLoading, isError, error, refetch } = useFinance({ from: range.from, to: range.to });

  const handleExport = useCallback(async (format: 'csv' | 'pdf') => {
    setExportLoading(true);
    try {
      const res = await api.get('/workshops/my/finance/export', {
        params: { format, from: range.from, to: range.to },
        responseType: 'blob',
      });
      const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mustauto-financeiro-${range.from}-${range.to}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // F-UI Phase 5: surface export failure (Sentry still receives it).
      console.error('[Finance] export failed', err);
      toast({
        type: 'error',
        title: 'Falha ao exportar financeiro',
        description: formatApiError(err, 'Não foi possível gerar o arquivo. Tente novamente em instantes.'),
      });
    } finally {
      setExportLoading(false);
    }
  }, [range.from, range.to, toast]);

  if (!user?.workshopId) {
    return (
    <EmptyState
      icon={<Building2 size={28} />}
      title="Sua conta não está vinculada a uma oficina"
      description="Isso costuma ser sessão desatualizada: recarregue a página. Se continuar, o suporte resolve o vínculo."
      action={{ label: 'Recarregar', onClick: () => window.location.reload() }}
      secondaryAction={{ label: 'Falar com o suporte', href: '/support' }}
    />
    );
  }

  if (isError && !data) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <SkeletonKPIGrid />
        <SkeletonTable rows={5} />
      </div>
    );
  }

  const { summary, orders, chartData } = data;
  const partsPerc =
    summary.totalRevenue > 0
      ? Math.round((summary.totalParts / summary.totalRevenue) * 100)
      : 0;
  const laborPerc =
    summary.totalRevenue > 0
      ? Math.round((summary.totalLabor / summary.totalRevenue) * 100)
      : 0;
  const outsourcedPerc =
    summary.totalRevenue > 0
      ? Math.round((summary.totalOutsourced / summary.totalRevenue) * 100)
      : 0;

  const chartFormatted = chartData.map((d) => ({
    ...d,
    date: formatDayMonthBR(d.date),
  }));

  return (
    <div className="space-y-6">
      {/* Header + filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Financeiro</h1>
          <p className="text-sm text-muted-foreground">
            {range.from} a {range.to}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {(['week', 'month', 'quarter'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  preset === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-foreground hover:bg-muted'
                }`}
              >
                {p === 'week' ? 'Semana' : p === 'month' ? 'Mês' : 'Trimestre'}
              </button>
            ))}
          </div>
          <ExportDropdown
            onExportCSV={() => handleExport('csv')}
            onExportPDF={() => handleExport('pdf')}
            loading={exportLoading}
          />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Faturamento Bruto"
          value={formatReais(summary.totalRevenue)}
          icon={<DollarSign size={20} />}
          subtitle={`${summary.completedCount} OS concluídas`}
        />
        <KPICard
          title="Peças"
          value={formatReais(summary.totalParts)}
          icon={<Package size={20} />}
          subtitle={`${partsPerc}% do total`}
        />
        <KPICard
          title="Mão de Obra"
          value={formatReais(summary.totalLabor)}
          icon={<Wrench size={20} />}
          subtitle={`${laborPerc}% do total`}
        />
        <KPICard
          title="Serviços Externos"
          value={formatReais(summary.totalOutsourced)}
          icon={<Truck size={20} />}
          subtitle={`${outsourcedPerc}% do total`}
        />
      </div>

      {/* Contas a Receber (spec 7.2) */}
      <ReceivablesSection />

      {/* Sinistros (Fase 4): o outro devedor da OS de seguradora. */}
      <InsurerReceivablesSection />

      <PayablesSection />

      {/* Revenue donut + Line chart side by side on large screens */}
      {summary.totalRevenue > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold text-foreground">
              Composicao da Receita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RevenueDonut
              parts={summary.totalParts}
              labor={summary.totalLabor}
              outsourced={summary.totalOutsourced}
            />
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Faturamento no Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartFormatted.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartFormatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} stroke="var(--color-border)" />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} stroke="var(--color-border)" />
                <Tooltip
                  formatter={(value) => formatReais(Number(value))}
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    borderColor: 'var(--color-border)',
                    borderRadius: '0.5rem',
                    color: 'var(--color-foreground)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Faturamento"
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              compact
              icon={<TrendingUp size={22} />}
              title="Sem dados no período"
              description="Escolha outro período no filtro acima para ver o faturamento."
            />
          )}
        </CardContent>
      </Card>

      {/* OS table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Ordens de Serviço
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {orders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Placa</TableHead>
                  <TableHead>Veículo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => {
                  const cfg = STATUS_MAP[o.status];
                  return (
                    <TableRow key={o.id}>
                      <TableCell className="font-medium">{o.number}</TableCell>
                      <TableCell>
                        {new Date(o.date).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="font-mono font-semibold">
                        {o.plate}
                      </TableCell>
                      <TableCell>{o.vehicleName}</TableCell>
                      <TableCell>{formatReais(o.total)}</TableCell>
                      <TableCell>
                        {cfg ? (
                          <Badge variant={cfg.variant}>{cfg.label}</Badge>
                        ) : (
                          <Badge variant="outline">{o.status}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              compact
              icon={<ClipboardList size={22} />}
              title="Nenhuma OS no período"
              description="Nenhuma ordem de serviço foi faturada entre as datas escolhidas."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
